import React, { ComponentProps, ReactNode, useCallback, useContext } from 'react';
import styled from 'styled-components';
import ActionButtonContext from '../../Context/ActionButtonContext';

import { CompoundButton as FluentCompoundButton, DefaultButton, IButtonProps, IRenderFunction } from '@fluentui/react';
import { Button as V9Button, makeStyles, mergeClasses, Tooltip } from '@fluentui/react-components';
import {
  BorderlessTheme,
  DangerTheme,
  InfoTheme,
  SecondaryTheme,
  SuccessTheme,
  WarningTheme,
} from '../../../styles/themes/default/defaultFluentTheme';

import { grey } from '../../../styles/themes/default/defaultTheme';
import { sizeMixin } from './ButtonMixins';
import { merge } from 'lodash';
import { IContextualMenuItem } from '@fluentui/react/src/components/ContextualMenu/ContextualMenu.types';

const ButtonWrapper = styled.div<{ inline?: boolean; small?: boolean; large?: boolean }>`
  position: relative;
  white-space: nowrap;
  vertical-align: middle;
  display: ${(props) => (props.inline ? 'inline-block' : 'block')};
  ${sizeMixin}
`;

export type ButtonPropsType = IButtonProps & {
  padding?: any;
  style?: any;
  children?: any;
  string?: string;
  minWidth?: any;
  small?: boolean;
  'data-testid'?: string;
  inline?: boolean;
  styles?: {
    wrapper?: React.CSSProperties;
  };
  tooltip?: string;
  tooltipProps?: Partial<ComponentProps<typeof Tooltip>>;
};

const useStyles = makeStyles({
  tooltip: {
    fontSize: '16px',
  },
  iconTaskbarButton: {
    height: '35px',
  },
});

export const Button = ({
  styles = {},
  padding = '6px 5px',
  style = {},
  inline,
  tooltip,
  tooltipProps,
  ...props
}: ButtonPropsType) => {
  const { root = {}, splitButtonMenuButton = {}, wrapper = {} } = styles;

  const griffelStyles = useStyles();

  const onRenderContextualMenuItem = useCallback<IRenderFunction<IContextualMenuItem>>(
    (menuItemProps, defaultRender) => {
      const content = menuItemProps?.tooltip ?? menuItemProps?.text ?? 'unknown';
      return (
        <Tooltip
          positioning={'before'}
          relationship={'label'}
          withArrow={true}
          content={{ children: content, className: griffelStyles.tooltip }}
        >
          <div>{defaultRender?.(menuItemProps) ?? menuItemProps?.text}</div>
        </Tooltip>
      );
    },
    [griffelStyles.tooltip]
  );

  const buttonContent = (
    <ButtonWrapper style={merge(style, wrapper, { padding: padding })} inline={inline}>
      <DefaultButton
        {...props}
        data-testid={props['data-testid'] || (typeof props.children === 'string' ? props.string : undefined)}
        styles={{
          ...styles,
          root: {
            borderColor: grey.base,
            borderRadius: '3px',
            minWidth: props.minWidth,
            height: props.small ? '26px' : '35px',
            minHeight: props.small ? '26px' : '32px',
            padding: props.small ? '0px 8px' : (root as any).padding,
            ...(root as any),
          },
          splitButtonMenuButton: {
            borderColor: grey.base,
            borderRadius: '3px',
            ...(splitButtonMenuButton as any),
          },
          rootDisabled: {
            backgroundColor: 'transparent',
            border: 'none',
          },
        }}
        menuProps={
          props.menuProps
            ? {
                ...props.menuProps,
                onRenderContextualMenuItem: onRenderContextualMenuItem,
              }
            : undefined
        }
      />
    </ButtonWrapper>
  );

  //Most buttons will have 'text' - But there are exceptions where 'element' is used, or certain buttons like the standard form 'Close'
  const tooltipContent = tooltip ?? props?.text;
  if (tooltipContent) {
    return (
      <Tooltip
        relationship={'label'}
        positioning={'below'}
        withArrow={true}
        content={{ children: tooltipContent, className: griffelStyles.tooltip }}
        {...tooltipProps}
      >
        {buttonContent}
      </Tooltip>
    );
  }
  return buttonContent;
};

export const CompoundButton = ({ styles = {}, padding = '6px 5px', style, ...props }: ButtonPropsType) => {
  const { root = {}, splitButtonMenuButton = {} } = styles;

  return (
    <ButtonWrapper style={merge(style, { padding: padding })}>
      <FluentCompoundButton
        {...props}
        data-testid={props['data-testid'] || (typeof props.children === 'string' ? props.string : undefined)}
        styles={{
          ...styles,
          root: {
            borderColor: grey.base,
            borderRadius: '3px',
            minWidth: props.minWidth,
            minHeight: '56px',
            ...(root as any),
          },
          splitButtonMenuButton: {
            borderColor: grey.base,
            borderRadius: '3px',
            ...(splitButtonMenuButton as any),
          },
        }}
      />
    </ButtonWrapper>
  );
};

export const DangerButton = (props: ButtonPropsType) => {
  return <Button {...props} theme={DangerTheme} />;
};

export const WarningButton = (props: ButtonPropsType) => {
  return <Button {...props} theme={WarningTheme} />;
};

export const InfoButton = (props: ButtonPropsType) => {
  return <Button {...props} theme={InfoTheme} />;
};

//TODO - Phase out. Can just use a regular <Button/> with the primary={true} prop from the fluent button.
export const PrimaryButton = (props: ButtonPropsType) => {
  return <Button {...props} primary />;
};

export const SuccessButton = (props: ButtonPropsType) => {
  return <Button {...props} theme={SuccessTheme} primary={true} />;
};

export const SecondaryButton = (props: ButtonPropsType) => {
  return <Button {...props} theme={SecondaryTheme} />;
};

export const BorderlessButton = (props: ButtonPropsType) => {
  return <Button {...props} theme={BorderlessTheme} styles={{ root: { border: 'none', width: '100%' } }} />;
};

/**
 * Wrap Button, but when disabled actually prevent onClick events from being called.
 */
export const DisableButton = ({ onClick, ...otherProps }) => {
  return (
    <Button
      onClick={
        otherProps.disable
          ? () => {
              console.warn('Clicked on a disabled button.');
            }
          : onClick
      }
      {...otherProps}
    />
  );
};

/**
 * Wrap InfoButton, but when disabled actually prevent onClick events from being called.
 */
export const DisableInfoButton = ({ onClick, ...otherProps }) => {
  return (
    <InfoButton
      onClick={
        otherProps.disable
          ? () => {
              console.warn('Clicked on a disabled button.');
            }
          : onClick
      }
      {...otherProps}
    />
  );
};

const LeftAlignedText = (props) => {
  return <div style={{ align: 'left', justifyContent: 'flex-start', textAlign: 'left' } as any}>{props.children}</div>;
};

type ActionButtonPropsType = IButtonProps & {
  actionButton?: any;
  disabled?: boolean;
  label?: string;
  'data-testid'?: string;
  testId?: string;
  individualButtonComponent?: ReactNode;
  primary?: boolean;
  buttonProps?: any;
  index?: number; //Workspace Actions.
  onClick?: Function;
};

export const ActionButton = ({
  actionButton,
  disabled,
  label,
  onClick,
  individualButtonComponent = InfoButton,
  primary,
  buttonProps,
  children,
  ...rest
}: ActionButtonPropsType) => {
  const { individualButtons } = useContext(ActionButtonContext);
  const Button: any = individualButtons && individualButtonComponent ? individualButtonComponent : BorderlessButton;

  //Note - Padding set to 0 for grouped buttons since the padding is effectively handled by the wrapping context menu.

  return (
    <Button
      data-testid={rest['data-testid'] || rest.testId}
      onClick={(e, meta) => {
        if (!disabled && onClick) {
          if (actionButton) {
            e.preventDefault();
            onClick(actionButton, meta);
          } else {
            onClick(e, meta);
          }
        }
      }}
      padding={individualButtons ? undefined : '0px 0px'}
      onRenderText={individualButtons ? undefined : LeftAlignedText}
      primary={primary}
      disabled={disabled}
      {...buttonProps}
    >
      {label || actionButton?.description || children}
    </Button>
  );
};

export const IconButton = ({
  icon,
  tooltip,
  onClick,
  size,
  isTaskbarIcon,
  children,
  className,
  wrapperStyle,
}: Pick<ComponentProps<typeof V9Button>, 'icon' | 'onClick' | 'size' | 'className'> & {
  isTaskbarIcon?: boolean;
  tooltip?: string;
  children?: ReactNode;
  wrapperStyle?: React.CSSProperties;
}) => {
  //TODO - Implement ButtonWrapper with useStyles/className on a div so we don't need to mix style/className props.
  const styles = useStyles();

  const button = (
    <ButtonWrapper
      inline={false}
      style={
        isTaskbarIcon
          ? {
              padding: '6px 5px',
              ...wrapperStyle,
            }
          : wrapperStyle
      }
    >
      <V9Button
        icon={icon}
        className={mergeClasses(isTaskbarIcon ? styles.iconTaskbarButton : undefined, className)}
        onClick={onClick}
        size={size}
        as={'button'}
      >
        {children}
      </V9Button>
    </ButtonWrapper>
  );
  if (tooltip) {
    return (
      <Tooltip content={tooltip} relationship={'label'}>
        {button}
      </Tooltip>
    );
  }
  return button;
};
